<script setup lang="ts">
import type { RemoveFieldsPayload, RowSchema } from '../utils/claim-editor'
import { resolveWidget } from '../utils/claim-widgets'

defineProps<{
  fields: RowSchema[]
}>()

const emit = defineEmits<{
  'remove-fields': [payload: RemoveFieldsPayload]
  'add-field': [name: string]
}>()

const getComponentBindings = (field: RowSchema) => ({
  name: field.name || field.editKey,
  attributes: {
    autocomplete: 'off',
    'data-1p-ignore': true,
    'data-lpignore': true,
    'data-no-autofill': true,
    placeholder: field.placeholder,
    required: field.isRequired,
    readonly: field.isReadonly,
  },
  label: field.label,
  ...(field.renderCollapsedValue && {
    renderCollapsedValue: field.renderCollapsedValue,
  }),
  ...(field.buttonLabels && { buttonLabels: field.buttonLabels }),
  ...(field.expandItems && { expandItems: field.expandItems }),
  ...(field.maxEntries && { maxEntries: field.maxEntries }),
  ...(field.refDataModel && { refDataModel: field.refDataModel }),
  ...(field.tupleSize && { tupleSize: field.tupleSize }),
})
</script>

<template>
  <section class="w-full">
    <h2
      class="bg-secondary-400 text-inverted z-1 sticky top-0 px-2 py-2 text-xs sm:px-4 lg:px-7"
    >
      Rialtic Only Fields
    </h2>
    <table
      v-if="fields.length"
      class="border-spacing-px! w-full border-separate"
    >
      <tbody>
        <template v-for="field in fields" :key="field.name">
          <!-- Code input renders multiple table rows + input components -->
          <component
            :is="resolveWidget(field.component!)"
            v-if="field.expandItems"
            v-bind="getComponentBindings(field)"
            @add-field="emit('add-field', $event)"
            @remove-fields="
              (keys: string[]) =>
                emit('remove-fields', {
                  metaField: field.name,
                  keys,
                  source: 'internal',
                })
            "
          />
          <tr v-else>
            <th
              class="bg-default outline-default w-[220px]! whitespace-nowrap px-2 py-2 text-left text-xs font-normal outline outline-1 sm:px-4 lg:px-7"
              scope="row"
            >
              {{ field.label }}
              <span v-if="field.isRequired" class="text-red-700">*</span>
            </th>
            <td
              v-if="field.isEditable"
              class="bg-neutral-50 p-0 outline outline-dashed outline-1 outline-neutral-500"
              :data-testid="`ClaimInternalFields__input-${field.name}`"
            >
              <component
                :is="resolveWidget(field.component!)"
                v-bind="getComponentBindings(field)"
              />
            </td>
            <td v-else class="bg-default p-2 text-xs">
              {{ String(field.modelValue ?? '') || '-' }}
            </td>
          </tr>
        </template>
      </tbody>
    </table>
  </section>
</template>
